// src/pages/AuthCallback.tsx
import { forgetRefreshSession } from '@/api/session-refresh';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useLoginWelcome } from '@/hooks/use-login-welcome';
import axios, { isAxiosError } from 'axios';
import { consumeAuthReturnPath } from '@/lib/auth-return';
import { AuthHeading, AuthNotice, AuthShell, AuthSpinner, authShellCopy } from '@/features/auth-shell';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/v1';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { setUser } = useApp();
  // Held in a ref so the callback effect does not run twice when the greeting
  // function changes identity after setUser.
  const greet = useLoginWelcome();
  const greetRef = useRef(greet);
  greetRef.current = greet;

  useEffect(() => {
    const handleCallback = async () => {
      const token = new URLSearchParams(window.location.search).get('token');

      if (!token) {
        console.error('Token manquant dans le callback');
        navigate(`/${lang || 'fr'}/login`);
        return;
      }

      try {
        // Stocker le token
        forgetRefreshSession();
        localStorage.setItem('yodi-token', token);

        // Récupérer le profil utilisateur
        const { data } = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUser(data);
        const firstLogin = new URLSearchParams(window.location.search).get('firstLogin');
        greetRef.current(data, firstLogin === '1' ? true : undefined);
        navigate(consumeAuthReturnPath() ?? `/${lang || 'fr'}/dashboard`);
      } catch (error: unknown) {
        console.error('Erreur lors du callback Google', error);
        if (isAxiosError(error) && error.response) {
          console.error('Response status:', error.response.status, 'data:', error.response.data);
        }
        navigate(`/${lang || 'fr'}/login`);
      }
    };

    handleCallback();
  }, [navigate, lang, setUser]);

  const copy = authShellCopy(lang);

  return (
    <AuthShell documentTitle={copy.callbackTitle}>
      <AuthHeading title={copy.callbackTitle} />
      <div role="status" aria-live="polite" data-testid="auth-callback-pending">
        <AuthNotice icon={<AuthSpinner className="size-5 text-brand" />} title={copy.callbackPending}>
          {copy.callbackHint}
        </AuthNotice>
      </div>
    </AuthShell>
  );
}
